import { Badge, Column, FlexBox, Page, RowId } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import type { SearchParameters } from 'lib-search-types';
import { getAsyncStatusFromRtkQueryStates } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect, useRef } from 'react';
import { useUpdateEffect } from 'react-use';
import { Writable } from 'ts-essentials';

import { searchApi } from 'apis/search';
import { ConversationsTable } from 'components/conversation';
import { ConversationsBulkToolbar } from 'components/conversation/conversationsBulkToolbar/conversationsBulkToolbar';
import { useSelectConversations } from 'utils/conversation/useSelectConversations/useSelectConversations';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { SearchFilters } from './components/filters/searchFilters';
import { SearchFiltersProvider } from './components/filters/searchFilters.context';
import { SearchInput } from './components/input/searchInput';
import { SavedSearchButton } from './components/savedSearch/button/savedSearchButton';
import { SavedSearchFormModal } from './components/savedSearch/form/savedSearchFormModal';
import { SavedSearchListModal } from './components/savedSearch/list/savedSearchListModal';
import { searchActions } from './search.actions';
import { maxResultsCount } from './utils/results/maxResultsCount';
import { createSearchUrlParams } from './utils/url/createSearchUrl';
import { useSearchInit } from './utils/useSearchInit/useSearchInit';

import styles from './search.view.module.scss';

export const SearchView = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  const flags = useFeatureFlag(['has-bulk-operations']);
  const { isInitialized } = useSearchInit();
  const { params } = useAppSelector((state) => state.search);

  const [
    readSearchResultsQuery,
    { asyncStatus, conversations, currentPage, totalPages, totalResults },
  ] = searchApi.useLazyReadAdvancedSearchResultsQuery({
    selectFromResult: ({
      data,
      isUninitialized,
      isFetching,
      isSuccess,
      isError,
    }) => ({
      asyncStatus: getAsyncStatusFromRtkQueryStates({
        isError,
        isFetching,
        isSuccess,
        isUninitialized,
      }),
      conversations: data?.data.results.map(({ result }) => result) ?? [],
      currentPage: data?.meta?.page ?? 1,
      totalPages: data?.meta?.total_pages ?? 1,
      totalResults: data?.meta?.total_results ?? 0,
    }),
  });

  const readSearchResultsQueryResultRef =
    useRef<ReturnType<typeof readSearchResultsQuery>>();

  const readSearchResults = useCallback(() => {
    if (readSearchResultsQueryResultRef.current) {
      readSearchResultsQueryResultRef.current.abort();
    }

    readSearchResultsQueryResultRef.current = readSearchResultsQuery({
      ...params,
      scope: 'conversations',
      enrichmentSpec: 'ALL',
    });
  }, [params, readSearchResultsQuery]);

  const {
    asyncStatus: selectConversationsAsyncStatus,
    eligibleBulkConversationIds,
    isCurrentPageAllConversationsSelected,
    isCurrentPageSomeConversationsSelected,
    selectedConversationIds,
    totalFilteredConversations,
    selectAllCurrentPageConversation,
    selectAllEligibleConversations,
    clearSelectedConversations,
    toggleSelectConversation,
    resetSelection,
  } = useSelectConversations({
    currentPageConversationIds: conversations.map(({ id }) => id) ?? [],
    onFetchConversationIdsBulk: async () => {
      const { data, meta } = await dispatch(
        searchApi.endpoints.readSelectiveSearch.initiate(
          {
            scope: 'conversations',
            query: params.query,
            filters: params.filters,
            isStrict: params.isStrict,
            selectFields: ['id'],
            pageSize: 1000,
          },
          { forceRefetch: true },
        ),
      ).unwrap();

      const csids = data.results.id?.map(Number) ?? [];

      return {
        csids,
        total_conversations: meta?.total_results,
        validations_failed: [],
      };
    },
  });

  const isAllEligibleConversationsSelected =
    selectedConversationIds.length &&
    selectedConversationIds.length === eligibleBulkConversationIds.length;

  useEffect(() => {
    if (isInitialized) {
      readSearchResults();
    }
  }, [isInitialized, readSearchResults]);

  // Handles resetting the selection when the search params change. This excludes the page param, as
  // this will change during pagination and we want to preserve the selection when that occurs.
  useUpdateEffect(() => {
    resetSelection();
  }, [params.query, params.filters, params.sortBy, params.isStrict]);

  const updateSearchParams = useCallback(
    (newParams: Partial<SearchParameters<'conversations'>>) => {
      dispatch(searchActions.params.update({ ...newParams, page: 1 }));
    },
    [dispatch],
  );

  const handleSubmitInput = useCallback(
    (query: string, isStrict: boolean) => {
      const nextParams: Partial<Writable<SearchParameters<'conversations'>>> = {
        query,
        isStrict,
      };

      if (!query) {
        nextParams.isStrict = false;
      }
      updateSearchParams(nextParams);

      ampli.searchFieldSubmitted({
        location: 'searchPage',
        isEmpty: !query,
        query,
      });
    },
    [updateSearchParams],
  );

  const handleClearInput = useCallback(() => {
    updateSearchParams({ query: '', isStrict: false });

    if (!params.filters.filters.length) {
      dispatch(searchActions.savedSearch.close());
    }

    ampli.searchFieldCleared({ location: 'searchPage' });
  }, [dispatch, params.filters.filters.length, updateSearchParams]);

  const handleClearSavedSearch = useCallback(() => {
    dispatch(searchActions.savedSearch.close());

    navigate({
      path: '/search/:scope',
      params: createSearchUrlParams({ scope: 'conversations' }),
    });

    ampli.savedSearchClosed();
  }, [dispatch, navigate]);

  const handleRowSelect = useCallback(
    (rowId: RowId) => {
      toggleSelectConversation(rowId as number);
    },
    [toggleSelectConversation],
  );

  const handleAllRowsSelect = useCallback(() => {
    if (isAllEligibleConversationsSelected) {
      resetSelection();
    } else {
      selectAllCurrentPageConversation();
    }
  }, [
    isAllEligibleConversationsSelected,
    resetSelection,
    selectAllCurrentPageConversation,
  ]);

  const handlePreviousPage = useCallback(() => {
    dispatch(searchActions.params.update({ page: currentPage - 1 }));
  }, [dispatch, currentPage]);

  const handleNextPage = useCallback(() => {
    dispatch(searchActions.params.update({ page: currentPage + 1 }));
  }, [dispatch, currentPage]);

  const showTotalResults =
    typeof totalResults === 'number' &&
    (!!params.query || params.filters.filters.length > 0);

  const showBulkActionsControls = selectedConversationIds.length > 0;

  const controls = (
    <div className={styles.controls}>
      {flags.hasBulkOperations && (
        <ConversationsBulkToolbar
          className={classNames(styles.control, styles.bulkActions, {
            [styles.active]: showBulkActionsControls,
          })}
          location="search"
          asyncStatus={selectConversationsAsyncStatus}
          eligibleBulkConversationIds={eligibleBulkConversationIds}
          selectedConversationIds={selectedConversationIds}
          totalFilteredConversations={totalFilteredConversations}
          onSelectAll={selectAllEligibleConversations}
          onClearAll={clearSelectedConversations}
        />
      )}

      <FlexBox
        className={classNames(styles.control, styles.filters, {
          [styles.active]: !showBulkActionsControls,
        })}
        gap="200"
        alignItems="center"
      >
        <SearchFiltersProvider>
          <SearchFilters />
        </SearchFiltersProvider>

        <SavedSearchButton
          buttonProps={{
            variant: 'secondary',
          }}
        />
      </FlexBox>
    </div>
  );

  return (
    <Page>
      <Column.Row className={styles.inputRow} noMinHeight>
        <SearchInput
          onSearch={handleSubmitInput}
          onClear={handleClearInput}
          onSavedSearchClear={handleClearSavedSearch}
        />
      </Column.Row>

      <Page.Head
        title={
          <FlexBox gap="200" alignItems="center">
            Conversations
            {showTotalResults && (
              <Badge variant="secondary" size="medium">
                {Math.min(totalResults, maxResultsCount)
                  .toLocaleString(navigator.language)
                  .concat(totalResults >= maxResultsCount ? '+' : '')}
              </Badge>
            )}
          </FlexBox>
        }
        controls={controls}
      />

      <Page.Body>
        <Page.Section>
          <ConversationsTable
            conversations={conversations}
            asyncStatus={asyncStatus}
            selectableRowsProps={{
              selectedRowIds: selectedConversationIds as number[],
              allRowsSelected:
                asyncStatus === 'fulfilled' &&
                isCurrentPageAllConversationsSelected,
              allRowsSelectedIndeterminate:
                asyncStatus === 'fulfilled' &&
                isCurrentPageSomeConversationsSelected,
              onRowSelect: handleRowSelect,
              onAllRowsSelect: handleAllRowsSelect,
            }}
            paginationProps={{
              previousButtonClick: handlePreviousPage,
              nextButtonClick: handleNextPage,
              previousButtonDisabled:
                currentPage <= 1 || asyncStatus === 'pending',
              nextButtonDisabled:
                currentPage >= totalPages || asyncStatus === 'pending',
            }}
          />
        </Page.Section>
      </Page.Body>

      <SavedSearchFormModal />
      <SavedSearchListModal />
    </Page>
  );
};
